import { Eye } from 'lucide-react';
import styled from 'styled-components';

import { IconButton } from '../../../../../../components/atoms/icon-button/icon-button';
import { ClienteVendaListItem } from '../../../../../../types/cliente-venda';
import { formatDateTime, formatPreco } from './format';
import { VendaStatusBadge } from './venda-status-badge';
import { VendaUpsellBadge } from './venda-upsell-badge';

interface Props {
  items: ClienteVendaListItem[];
  onOpen: (id: string) => void;
}

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.textMuted};
    background: ${({ theme }) => theme.colors.bgMuted};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    font-weight: 600;
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  th:last-child,
  td:last-child {
    text-align: right;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  strong {
    font-weight: 600;
  }
  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12.5px;
  }
`;

const ValueCell = styled.div`
  font-variant-numeric: tabular-nums;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
`;

const SrOnly = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const buyerName = (item: ClienteVendaListItem): string =>
  `${item.comprador.nome ?? ''} ${item.comprador.sobrenome ?? ''}`.trim() ||
  '—';

export const VendasTable = ({ items, onOpen }: Props) => (
  <Table>
    <SrOnly>Vendas do cliente</SrOnly>
    <thead>
      <tr>
        <th scope="col">Data</th>
        <th scope="col">Produto</th>
        <th scope="col">Valor</th>
        <th scope="col">Comprador</th>
        <th scope="col">Status</th>
        <th scope="col">Ações</th>
      </tr>
    </thead>
    <tbody>
      {items.map((v) => (
        <tr key={v.id} data-testid={`venda-row-${v.id}`}>
          <td>
            <Cell>
              <strong>{formatDateTime(v.createdAt)}</strong>
              <small>#{v.id.slice(0, 8)}</small>
            </Cell>
          </td>
          <td>{v.produto.nome ?? '—'}</td>
          <td>
            <ValueCell>
              <strong>{formatPreco(v.valor, v.moedaSigla)}</strong>
              {v.metodoPagamentoDescricao && (
                <small style={{ color: 'inherit' }}>
                  {v.metodoPagamentoDescricao}
                </small>
              )}
            </ValueCell>
          </td>
          <td>
            <Cell>
              <strong>{buyerName(v)}</strong>
              {v.comprador.email && <small>{v.comprador.email}</small>}
            </Cell>
          </td>
          <td>
            <BadgeRow>
              <VendaStatusBadge
                status={v.status}
                descricao={v.statusDescricao}
              />
              <VendaUpsellBadge upsell={v.upsell} />
            </BadgeRow>
          </td>
          <td>
            <IconButton
              label={`Ver detalhes da venda ${v.id.slice(0, 8)}`}
              onClick={() => onOpen(v.id)}
            >
              <Eye size={16} />
            </IconButton>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);
